import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "utils";
import { Icon } from "@/components/icon/Icon";
import { ClaimVerification } from "@/types/summary";

type AnnotationType =
  "quote" | "content_flag" | "motif" | "theme" | "trope" | "claim";

interface BaseAnnotationProps {
  type: AnnotationType;
  content?: string;
  quotedContent?: string;
  categories?: string[];
}

interface ClaimAnnotationProps extends BaseAnnotationProps {
  type: "claim";
  claim: ClaimVerification;
  onExpandToggle?: () => void;
}

type AnnotationContentProps = BaseAnnotationProps | ClaimAnnotationProps;

function isClaimAnnotation(
  props: AnnotationContentProps,
): props is ClaimAnnotationProps {
  return props.type === "claim";
}

export function AnnotationContent(props: AnnotationContentProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Configuration for all annotation types
  const getConfig = () => {
    const { type } = props;
    if (isClaimAnnotation(props)) {
      const { claim } = props;
      const { assessment } = claim;
      switch (assessment) {
        case "support":
          return {
            bgColor: "bg-primary-25",
            iconColor: "text-primary-700",
            label: "Supported",
            icon: "circle-check" as const,
          };
        case "partially_support":
          return {
            bgColor: "bg-amber-50",
            iconColor: "text-amber-700",
            label: "Partially Supported",
            icon: "circle-check" as const,
          };
        default:
          return {
            bgColor: "bg-secondary-25",
            iconColor: "text-secondary-700",
            label: "Unknown",
            icon: "circle-check" as const,
          };
      }
    }

    const { categories } = props;
    // Regular annotation types
    switch (type) {
      case "quote":
        return {
          bgColor: "bg-primary-25",
          iconColor: "text-indigo-500",
          label: "Quote",
          icon: "message-circle" as const,
        };
      case "content_flag":
        return {
          bgColor: "bg-amber-50",
          iconColor: "text-orange-400",
          label: categories?.[0] || "Content Flag",
          icon: "triangle-alert" as const,
        };
      case "motif":
        return {
          bgColor: "bg-primary-25",
          iconColor: "text-purple-500",
          label: "Motif",
          icon: "target" as const,
        };
      case "theme":
        return {
          bgColor: "bg-primary-25",
          iconColor: "text-green-500",
          label: "Theme",
          icon: "layers" as const,
        };
      case "trope":
        return {
          bgColor: "bg-primary-25",
          iconColor: "text-amber-500",
          label: "Trope",
          icon: "star" as const,
        };
      default:
        return {
          bgColor: "bg-primary-25",
          iconColor: "text-gray-500",
          label: type,
          icon: "circle" as const,
        };
    }
  };

  const config = getConfig();

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
    if (isClaimAnnotation(props)) {
      const { onExpandToggle } = props;
      if (onExpandToggle) {
        setTimeout(() => onExpandToggle(), 0);
      }
    }
  };

  return (
    <div className="flex flex-col gap-4 text-[14px]">
      {/* Header with Icon and Label */}
      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl p-4 font-medium",
          config.bgColor,
        )}
      >
        <Icon
          name={config.icon}
          size="large"
          iconClassName={config.iconColor}
        />
        <span className="text-gray-900">{config.label}</span>
      </div>

      {/* Content */}
      <div className="px-2">
        {/* Regular annotation content */}
        {!isClaimAnnotation(props) &&
          (() => {
            const { content, quotedContent } = props;
            return (
              <>
                {content && <h4 className="text-gray-900 mb-2">{content}</h4>}
                {quotedContent && (
                  <p className="text-gray-800 leading-relaxed italic">
                    &ldquo;{quotedContent}&rdquo;
                  </p>
                )}
              </>
            );
          })()}

        {/* Claim content */}
        {isClaimAnnotation(props) &&
          (() => {
            const { claim } = props;
            return (
              <>
                <h4 className="text-gray-900 mb-2 leading-relaxed">
                  {claim.claim}
                </h4>

                {/* Expand/Collapse button */}
                <button
                  type="button"
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExpandToggle();
                  }}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp size={16} />
                      <span className="text-sm">Less details</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      <span className="text-sm">More details</span>
                    </>
                  )}
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 text-gray-800">
                    {/* Questions */}
                    {claim.questions.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Verification Questions:
                        </p>
                        <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                          {claim.questions.map((q) => (
                            <li key={q}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Evidence */}
                    {claim.evidence.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Evidence:
                        </p>
                        <div className="space-y-2">
                          {claim.evidence.map((e) => (
                            <div
                              key={`evidence-${e.slice(0, 50)}`}
                              className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-200"
                            >
                              &quot;{e.slice(0, 150)}
                              {e.length > 150 ? "..." : ""}&quot;
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Answers */}
                    {claim.answers.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Answers:
                        </p>
                        <div className="space-y-2">
                          {claim.answers.map((a) => (
                            <div
                              key={`answer-${a.slice(0, 50)}`}
                              className="text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-200"
                            >
                              {a}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
      </div>
    </div>
  );
}
